"use client";

import {
  MindstringPrototype,
  Situation,
  SelectedSituation,
  SelectedThought,
} from "@/generated/fastapi";
import { useCallback } from "react";
import type { StudioLayoutMap } from "../types";
import { sortSituations } from "@/features/situations";

export default function useStudioLayoutMap() {
  const buildStudioLayoutMap = useCallback(
    (situations: Situation[]): StudioLayoutMap => {
      const result: Record<MindstringPrototype, string[]> = {
        job_activity: [],
        learning_outcome: [],
        workplace_characteristic: [],
        individual_description: [],
      };

      // Sort situations to match backend order (futures first, then chronological)
      const sortedSituations = sortSituations(situations);

      // Loop through each situation and map its prototypes to situation IDs
      for (const situation of sortedSituations) {
        for (const prototype of situation.prototypes) {
          result[prototype].push(situation.id);
        }
      }

      return result;
    },
    [],
  );

  const buildStudioLayoutMapFromSelection = useCallback(
    (
      availableSituations: Situation[],
      selectedSituations: SelectedSituation[], // works with SelectedSituationId[] !!!!
      selectedThoughts?: SelectedThought[] | undefined, // refactored to use SelectedThought[]
    ): StudioLayoutMap => {
      // Build a Set of all selected situation IDs for quick lookup
      const selectedIdSet = new Set<string>();
      for (const selected of selectedSituations) {
        for (const situation_id of selected.situations_ids) {
          selectedIdSet.add(situation_id);
        }
      }

      // Get full situation objects from global store (already sorted by backend)
      // and filter to only include selected situations
      const fullSituationsMap = new Map<string, Situation>();

      // Iterate through situations in their original backend order
      for (const situation of availableSituations) {
        if (selectedIdSet.has(situation.id)) {
          fullSituationsMap.set(situation.id, situation);
        }
      }

      // Build situations array with proper prototype filtering
      const situationsArray: Situation[] = [];
      const situationIdSet = new Set<string>();

      for (const selected of selectedSituations) {
        for (const situation_id of selected.situations_ids) {
          const fullSituation = fullSituationsMap.get(situation_id);
          if (!fullSituation) continue; // Skip if situation not found in store

          if (!situationIdSet.has(situation_id)) {
            // Add situation with only the current prototype
            situationsArray.push({
              ...fullSituation,
              prototypes: [selected.prototype],
            });
            situationIdSet.add(situation_id);
          } else {
            // Append prototype to existing situation
            const situation = situationsArray.find(
              (s) => s.id === situation_id,
            );
            if (
              situation &&
              !situation.prototypes.includes(selected.prototype)
            ) {
              situation.prototypes.push(selected.prototype);
            }
          }
        }
      }

      // Sort situations using backend logic (futures first, then chronological)
      // Now this works properly because situations have all required date fields
      const sortedSituations = sortSituations(situationsArray);

      // Now build the layout with sorted situations
      const layout = buildStudioLayoutMap(sortedSituations);

      // If selectedThoughts, apply function to filter out empty situations
      if (selectedThoughts) {
        for (const prototype in layout) {
          layout[prototype as MindstringPrototype] = layout[
            prototype as MindstringPrototype
          ].filter((situationId) =>
            selectedThoughts.some(
              (st) =>
                st.prototype === prototype && st.situation_id === situationId,
            ),
          );
        }
      }

      return layout;
    },
    [buildStudioLayoutMap],
  );

  return {
    buildStudioLayoutMap,
    buildStudioLayoutMapFromSelection,
  };
}
